import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import { AvailableForSelectedDatesEnum } from '@rover/rsdk/src/apiClient/latest/model/availableForSelectedDatesEnum';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyCondensed,
  getIsPriceTransparencyFull,
  getIsPriceTransparencyLite,
} from '@rover/utilities/priceTransparency';

import {
  AlertContentEnum,
  getAlertContent,
  getSubHeader,
  HEADER_TITLES,
  HeaderIconEnum,
  HeaderTitlesEnum,
  SubHeadersEnum,
} from './constants';
import { BestMatchBuilderProps, PrebuiltHeaderProps } from './types';

interface Props extends BestMatchBuilderProps {
  hasPets: boolean;
  isBestMatchExperience?: boolean;
  isBestMatchesSearch?: boolean;
  isInitialPage?: boolean;
  isSeoPage?: boolean;
  isRepeat?: boolean;
  selectedProvider?: OptimizedSearchResult;
}

const buildMainSearchResultsHeader = (props: Props): PrebuiltHeaderProps | object | null => {
  const { startDate, endDate } = props.dateData;
  const availability =
    props.selectedProvider?.availableForSelectedDates || AvailableForSelectedDatesEnum.unknown;
  const hasDates = (!!startDate && !!endDate) || (props.isRepeat && !!startDate);
  const hasSelectedProvider = !!props.selectedProvider;
  const isSelectedProviderAvailable = availability === AvailableForSelectedDatesEnum.available;
  const petCount = props.petCount || 0;
  const isPriceTransparency = getIsPriceTransparency(props.priceTransparencyLevel);
  const isPtCondensed = getIsPriceTransparencyCondensed(props.priceTransparencyLevel);

  // Early return when headers should not be displayed
  const shouldSkipHeader =
    !hasSelectedProvider &&
    !props.isBestMatchExperience &&
    hasDates &&
    petCount > 0 &&
    (!isPriceTransparency || isPtCondensed);

  if (shouldSkipHeader) {
    return null;
  }

  let icon: HeaderIconEnum | undefined;
  let title: string | undefined;
  let primarySubheader: string | undefined;
  let alertText: string | null | undefined;
  let isBestMatchHeader = false;

  const isPtLite = getIsPriceTransparencyLite(props.priceTransparencyLevel);
  const isPtFull = getIsPriceTransparencyFull(props.priceTransparencyLevel);

  // For SEO pages
  const handleSeoPage = (): void => {
    icon = HeaderIconEnum.SEARCH;
    title = HEADER_TITLES()[HeaderTitlesEnum.FIND_MATCH];

    if (isPtFull) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_SEO_PAGE, props);
    } else if (isPtLite) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_LITE_SEO_PAGE, props);
    }
  };

  // For scenarios where dates are not selected
  const handleWithNoDates = (): void => {
    icon = HeaderIconEnum.SEARCH;
    title = HEADER_TITLES()[HeaderTitlesEnum.FIND_MATCH];

    if (isPtFull && !isPtCondensed) {
      alertText = hasSelectedProvider
        ? null
        : getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES, props);
    } else {
      primarySubheader = getSubHeader(SubHeadersEnum.ADD_DATES, props);
    }
  };

  // For scenarios that show pricing with dates
  const handlePricesForUnit = (): void => {
    if (isPtFull && !isPtCondensed) {
      alertText = hasSelectedProvider
        ? null
        : getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT, props);
    } else {
      primarySubheader = getSubHeader(SubHeadersEnum.SEEING_PROVIDERS_DATES, props);
    }
  };

  // For selected provider scenarios
  const handleWithSelectedProvider = (): void => {
    icon = HeaderIconEnum.MAP;

    if (props.isBestMatchExperience) {
      if (!hasDates) {
        handleWithNoDates();
        return;
      }

      if (props.isBestMatchesSearch && !props.hasBestMatches) {
        // If it's a best matches search, then this header title will be based on
        // the absence of best match results.
        title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
      } else if (!props.hasBestMatches) {
        if (props.isInitialPage) {
          // This header renders if there are no best match results, regardless of
          // whether it's a best matches search.
          icon = HeaderIconEnum.BOLT;
          title = HEADER_TITLES()[HeaderTitlesEnum.BEST_MATCHES];
          isBestMatchHeader = true;
        } else {
          title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
          primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

          return;
        }
      }

      handlePricesForUnit();
      return;
    }

    if (isPriceTransparency) {
      title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];

      if (hasDates) {
        primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);
      }

      return;
    }

    if (hasDates && !isSelectedProviderAvailable) {
      title = HEADER_TITLES()[HeaderTitlesEnum.AVAILABLE_SITTERS_NEAR_YOU];
      primarySubheader = getSubHeader(SubHeadersEnum.POST_SELECTED_SITTER_RESULTS, props);

      return;
    }

    if (hasDates) {
      title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_AVAILABLE_SITTERS_IN_AREA];
      primarySubheader = getSubHeader(SubHeadersEnum.POST_SELECTED_SITTER_RESULTS, props);
    } else {
      title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
      primarySubheader = getSubHeader(SubHeadersEnum.ADD_DATES, props);
    }
  };

  // For the rest of the search results, regardless of best matches
  const handleStandardHeaders = (): void => {
    icon = HeaderIconEnum.MAP;

    if (props.isBestMatchExperience) {
      if (props.isBestMatchesSearch) {
        if (props.hasBestMatches) {
          title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
          primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

          return;
        }

        // only if no results
        title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
      } else if (props.hasBestMatches || (!props.isBestMatchesSearch && !props.isInitialPage)) {
        title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
        primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

        return;
      } else {
        icon = HeaderIconEnum.BOLT;
        title = HEADER_TITLES()[HeaderTitlesEnum.BEST_MATCHES];
        isBestMatchHeader = true;
      }
    } else if (hasDates && ((isPtFull && !isPtCondensed) || petCount > 0)) {
      title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
      icon = HeaderIconEnum.MAP;
    } else {
      title = HEADER_TITLES()[HeaderTitlesEnum.FIND_MATCH];
      icon = HeaderIconEnum.SEARCH;
    }

    handlePricesForUnit();
  };

  if (hasSelectedProvider) {
    handleWithSelectedProvider();
  } else if (props.isSeoPage) {
    if (!isPriceTransparency) {
      return null;
    }
    handleSeoPage();
  } else if (!hasDates) {
    handleWithNoDates();
  }

  if (!title) {
    handleStandardHeaders();
  }

  return {
    icon,
    title,
    primarySubheader,
    alertText,
    alertSeverity: hasDates && petCount > 0 ? AlertSeverity.INFO : AlertSeverity.WARNING,
    isBestMatchHeader,
  };
};

export default buildMainSearchResultsHeader;
